import { useState, useEffect } from "react";
import { domain, tiers } from "../../../consts";

const StepFinalSettings = ({ formData, updateFormData }) => {
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [slugStatus, setSlugStatus] = useState(null);
  const [slugError, setSlugError] = useState("");

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-&]/g, "")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");
  };

  useEffect(() => {
    if (!formData.slug && formData.eventName) {
      const suggestedSlug = generateSlug(formData.eventName);
      updateFormData({ slug: suggestedSlug });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSlugAvailability = async (slug) => {
    if (!slug) {
      setSlugStatus(null);
      return;
    }

    const urlPattern = /^[a-z0-9&]+(?:-[a-z0-9&]+)*$/;
    if (!urlPattern.test(slug)) {
      setSlugStatus("invalid");
      setSlugError("URL može sadržavati samo mala slova, brojeve, & i crtice");
      return;
    }

    setIsCheckingSlug(true);
    try {
      const response = await fetch(`${domain}/rsvp/check-slug/${slug}`, {
        credentials: "include",
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (response.ok) {
        setSlugStatus("available");
        setSlugError("");
      } else {
        setSlugStatus("taken");
        setSlugError("Ovaj URL je već zauzet");
      }
    } catch (error) {
      setSlugStatus("error");
      setSlugError("Greška pri provjeri dostupnosti");
    } finally {
      setIsCheckingSlug(false);
    }
  };

  const handleSlugChange = (value) => {
    const formattedSlug = value.toLowerCase().replace(/\s+/g, "-");
    updateFormData({ slug: formattedSlug });
    
    const timeoutId = setTimeout(() => {
      checkSlugAvailability(formattedSlug);
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-50 mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900">Gotovo za objavu!</h3>
        <p className="text-sm text-gray-500 mt-1">Postavite URL i odaberite paket</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          URL pozivnice
        </label>
        <div className="relative">
          <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500 text-sm">
            pozivnica.hr/
          </span>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            placeholder="klara-i-ivan"
            className={`w-full pl-28 pr-10 py-3 border rounded-xl text-sm focus:outline-none focus:ring-2 transition-colors ${
              slugStatus === "available"
                ? "border-green-500 focus:ring-green-500"
                : slugStatus === "taken" || slugStatus === "invalid"
                ? "border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:ring-teal-500"
            }`}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {isCheckingSlug && (
              <svg className="animate-spin h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            {!isCheckingSlug && slugStatus === "available" && (
              <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            )}
            {!isCheckingSlug && (slugStatus === "taken" || slugStatus === "invalid") && (
              <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </div>
        {slugError && (
          <p className="mt-1 text-sm text-red-600">{slugError}</p>
        )}
        {slugStatus === "available" && (
          <p className="mt-1 text-sm text-green-600">Ovaj URL je dostupan!</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Odaberite paket
        </label>
        <div className="space-y-3">
          {tiers.map((tier) => (
            <label
              key={tier.value}
              className={`relative flex items-start p-4 border rounded-xl cursor-pointer transition-all ${
                formData.tier === tier.value
                  ? "border-teal-500 bg-teal-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="tier"
                value={tier.value}
                checked={formData.tier === tier.value}
                onChange={(e) => updateFormData({ tier: parseInt(e.target.value) })}
                disabled={formData.id && formData.paymentVerified}
                className="sr-only"
              />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">
                    {tier.value === 0 && "Besplatni"}
                    {tier.value === 1 && "Prošireni"}
                    {tier.value === 2 && "Premium"}
                  </span>
                  <span className={`font-semibold ${tier.value === 0 ? "text-green-600" : "text-gray-900"}`}>
                    {tier.value === 0 && "0€"}
                    {tier.value === 1 && "29€"}
                    {tier.value === 2 && "69€"}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {tier.value === 0 && "Do 20 uzvanika"}
                  {tier.value === 1 && "Do 80 uzvanika"}
                  {tier.value === 2 && "Neograničen broj uzvanika"}
                </p>
              </div>
              {formData.tier === tier.value && (
                <div className="ml-3">
                  <div className="w-5 h-5 bg-teal-500 rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              )}
            </label>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-sm font-medium text-gray-900 mb-4">
          Dijeljenje na društvenim mrežama
        </h4>
        <p className="text-xs text-gray-500 mb-4">
          Ove informacije se prikazuju kad podijelite pozivnicu na WhatsApp, Facebook, itd.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Naslov
            </label>
            <input
              type="text"
              value={formData.shareTitle}
              onChange={(e) => updateFormData({ shareTitle: e.target.value })}
              placeholder={formData.eventName || "npr. Vjenčanje Klare i Ivana"}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Opis
            </label>
            <input
              type="text"
              value={formData.shareDescription}
              onChange={(e) => updateFormData({ shareDescription: e.target.value })}
              placeholder="npr. Pozivamo vas da uljepšate ovaj poseban dan s nama!"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-sm font-medium text-gray-900 mb-4">
          RSVP forma
        </h4>

        <div className="space-y-3">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700">Traži email adresu</span>
            <input
              type="checkbox"
              checked={formData.includeEmail}
              onChange={(e) => updateFormData({ includeEmail: e.target.checked })}
              className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700">Traži broj telefona</span>
            <input
              type="checkbox"
              checked={formData.includePhone}
              onChange={(e) => updateFormData({ includePhone: e.target.checked })}
              className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
          </label>

          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm text-gray-700">Ponudi opciju smještaja</span>
            <input
              type="checkbox"
              checked={formData.formAccommodation}
              onChange={(e) => updateFormData({ formAccommodation: e.target.checked })}
              className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
            />
          </label>
        </div>
      </div>

      <div className="bg-teal-50 rounded-xl p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-teal-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="ml-3">
            <p className="text-sm font-medium text-teal-800">Sve je spremno!</p>
            <p className="text-sm text-teal-700 mt-1">
              Kliknite &ldquo;Kreiraj pozivnicu&rdquo; da objavite vašu pozivnicu. Možete je uređivati i nakon objave.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepFinalSettings;
